// Required imports
import React, { useEffect } from "react";
import { ResponsivePie } from "@nivo/pie";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  ElementContainer,
  ColumnContainer,
  Card,
} from "../../../components/containers/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { createCenteredValueLayer } from "./CenteredText";

import { darkGreen, votingAlignmentBarsPalette } from "../../../styles";
import { StewardshipFilterParam } from "../../../models/stewardshipModels";
import { usePost } from "../../../hooks/usePost";
import { formatPct, toSlug } from "../../../utils/formattingUtils";
import { ExportButton } from "../../../components/buttons/ExportButton";
import { VOTING_EXECUTION_EXPORT_CONFIG } from "../constants";
import {
  VOTING_PIE_CARD_HEIGHT,
  VOTING_PIE_INNER_RADIUS,
  VOTING_PIE_MARGIN,
  VotingPieChartContainer,
  VotingPieEmpty,
  VotingPieLegendContainer,
} from "./VotingPieLayout";

enum VOTING_EXECUTION {
  VOTED = "Voted",
  NOT_VOTED = "Not Voted",
}

const COLORS_BY_VOTING_EXECUTION = {
  [VOTING_EXECUTION.VOTED]: votingAlignmentBarsPalette[0],
  [VOTING_EXECUTION.NOT_VOTED]: votingAlignmentBarsPalette[1],
};

// The main component that renders the ResponsivePie chart
export const VotingExecutionPie: React.FC<{
  filters: StewardshipFilterParam;
}> = ({ filters }) => {
  const [pieData, setPieData] = React.useState<PieDatum[]>([]);

  const {
    data,
    isLoading,
    sendData: getVotingExecutionData,
  } = usePost<
    { voting_status: VOTING_EXECUTION; total_count: number }[],
    StewardshipFilterParam
  >();

  useEffect(() => {
    getVotingExecutionData("GET_VOTING_EXECUTION", filters);
  }, [filters]);

  useEffect(() => {
    if (data) {
      const formattedData: PieDatum[] = data.map((item) => ({
        id: toSlug(item.voting_status),
        label: item.voting_status,
        value: item.total_count as unknown as string,
        color: COLORS_BY_VOTING_EXECUTION[item.voting_status],
      }));
      setPieData(formattedData);
    }
  }, [data]);

  // Transforming data into a format that can be used by Nivo Pie
  const totalVotes: number = (data || []).reduce(
    (sum, data) => sum + data.total_count,
    0
  );
  const votingExecutionPercentage: string =
    totalVotes > 0
      ? formatPct(
          (data?.find((item) => item?.voting_status === VOTING_EXECUTION.VOTED)
            ?.total_count || 0) / totalVotes
        )
      : "";

  // eslint-disable-next-line
  const customLayers: any = [
    createCenteredValueLayer(votingExecutionPercentage, true),
    createCenteredValueLayer(data ? "voting execution" : "", false),
  ];

  const layersConfig = layers.concat(customLayers);

  return (
    <Card
      h={VOTING_PIE_CARD_HEIGHT}
      isLoading={isLoading}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Voting Execution
      </CompassParagraph>
      {pieData.length > 0 ? (
        <ElementContainer ai="flex-start">
          <VotingPieChartContainer>
            <ResponsivePie
              theme={theme}
              data={pieData}
              margin={VOTING_PIE_MARGIN}
              innerRadius={VOTING_PIE_INNER_RADIUS}
              layers={layersConfig as any} //eslint-disable-line
              activeOuterRadiusOffset={4}
              borderWidth={1}
              colors={({ data }): string => data.color as string}
              borderColor={{ from: "color", modifiers: [["darker", 0]] }}
              arcLabelsSkipAngle={360}
              arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
              enableArcLinkLabels={false}
              defs={definitions}
              fill={fills}
              legends={[]}
            />
          </VotingPieChartContainer>
          <VotingPieLegendContainer>
            <ColumnContainer
              mt={1}
              jc={pieData.length > 8 ? "flex-start" : "center"}
              ai="flex-start"
              ml={0}
            >
              {pieData.map((item) => (
                <ElementContainer key={item.id} mb={5}>
                  <LegendCircle color={item.color as string} />
                  <CompassParagraph color="#8B909A" fontSize={12}>
                    {item.label}: {item.value}
                  </CompassParagraph>
                </ElementContainer>
              ))}
            </ColumnContainer>
          </VotingPieLegendContainer>
        </ElementContainer>
      ) : (
        !isLoading && <VotingPieEmpty>No voting data</VotingPieEmpty>
      )}
      {!isLoading && pieData.length > 0 && (
        <ExportButton data={pieData} {...VOTING_EXECUTION_EXPORT_CONFIG} />
      )}
    </Card>
  );
};

// Configurations for the Nivo Pie chart
const theme = {
  legends: { text: { fontFamily: "Apercu", color: "greyText" } },
};

const layers = ["arcs", "arcLinkLabels", "legends"];

const definitions = [
  {
    id: "dots",
    type: "patternDots",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    size: 4,
    padding: 1,
    stagger: true,
  },
  {
    id: "lines",
    type: "patternLines",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    rotation: -45,
    lineWidth: 6,
    spacing: 10,
  },
];

const fills = [
  { match: { id: "dots" }, id: "dots" },
  { match: { id: "lines" }, id: "lines" },
];

type PieDatum = {
  id: string | number;
  value: string;
  label?: string;
  color?: string;
};
